import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Gauge, AlarmClock, TriangleAlert, Users2, Clock, Flag, CheckCircle2, Loader2, ArrowRight,
} from "lucide-react";

function daysLabel(d) {
  if (d == null) return "—";
  return d < 1 ? "<1 day" : `${Math.round(d)} day${Math.round(d) === 1 ? "" : "s"}`;
}

export default function Oversight() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [queue, setQueue] = useState(null);
  const [flags, setFlags] = useState([]);
  const [openTotal, setOpenTotal] = useState(0);
  const [flagsLog, setFlagsLog] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [flagItem, setFlagItem] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    const [a, q, f] = await Promise.allSettled([
      api.get("/analytics"), api.get("/review-queue"), api.get("/flags?status=open&page_size=200"),
    ]);
    if (a.status === "fulfilled") setAnalytics(a.value.data);
    if (q.status === "fulfilled") setQueue(q.value.data);
    if (f.status === "fulfilled") { setFlags(f.value.data.items || []); setOpenTotal(f.value.data.total || 0); }
  }, []);
  const loadFlagsLog = useCallback(async (page = 1) => {
    try { const { data } = await api.get(`/flags?page=${page}&page_size=8`); setFlagsLog(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadAll(); loadFlagsLog(1); }, [loadAll, loadFlagsLog]);

  const flaggedChapterIds = new Set(flags.filter((f) => f.status === "open").map((f) => f.chapter_id));

  const submitFlag = async () => {
    setBusy(true);
    try {
      await api.post(`/chapters/${flagItem.chapter_id}/flag`, { note });
      toast.success("Delay flagged — supervisor has been notified");
      setFlagItem(null); setNote("");
      loadAll(); loadFlagsLog(flagsLog.page);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const leaderboard = analytics?.turnaround_leaderboard || [];
  const loadMap = Object.fromEntries((analytics?.supervisor_load || []).map((s) => [s.name, s.load]));
  const supervisorNames = Array.from(new Set([...leaderboard.map((r) => r.name), ...Object.keys(loadMap)]));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="oversight-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg brand-bg text-white flex items-center justify-center"><Gauge className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Research Oversight</h1>
          <p className="text-muted-foreground">Monitor supervisor activity and flag delays across the institution.</p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="oversight-summary">
        <Card className="p-5"><Users2 className="h-5 w-5 brand-text" /><p className="text-3xl font-heading font-bold mt-2">{supervisorNames.length}</p><p className="text-xs text-muted-foreground">Active supervisors</p></Card>
        <Card className="p-5"><Clock className="h-5 w-5 text-amber-600" /><p className="text-3xl font-heading font-bold mt-2">{queue?.items?.length ?? "—"}</p><p className="text-xs text-muted-foreground">Chapters awaiting review</p></Card>
        <Card className="p-5"><TriangleAlert className="h-5 w-5 text-red-600" /><p className="text-3xl font-heading font-bold mt-2">{queue?.overdue_count ?? "—"}</p><p className="text-xs text-muted-foreground">Overdue ({queue?.sla_days ?? 7}+ days)</p></Card>
        <Card className="p-5"><Flag className="h-5 w-5 brand-text" /><p className="text-3xl font-heading font-bold mt-2">{openTotal}</p><p className="text-xs text-muted-foreground">Open delay flags</p></Card>
      </div>

      {/* Supervisor activity */}
      <Card className="p-6" data-testid="supervisor-activity">
        <div className="flex items-center gap-2 mb-4"><Users2 className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Supervisor activity & turnaround</h3></div>
        {supervisorNames.length === 0 ? (
          <p className="text-sm text-muted-foreground">No supervisor activity to show yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium">Supervisor</th>
                  <th className="py-2 px-4 font-medium text-right">Active load</th>
                  <th className="py-2 px-4 font-medium text-right">Awaiting review</th>
                  <th className="py-2 px-4 font-medium text-right">Overdue</th>
                  <th className="py-2 px-4 font-medium text-right">Avg wait</th>
                  <th className="py-2 pl-4 font-medium text-right">Oldest</th>
                </tr>
              </thead>
              <tbody>
                {supervisorNames.map((name) => {
                  const r = leaderboard.find((x) => x.name === name) || {};
                  return (
                    <tr key={name} className="border-b border-border/60" data-testid={`activity-row-${name}`}>
                      <td className="py-2 pr-4 font-medium text-foreground">{name}</td>
                      <td className="py-2 px-4 text-right">{loadMap[name] ?? 0}</td>
                      <td className="py-2 px-4 text-right">{r.awaiting ?? 0}</td>
                      <td className="py-2 px-4 text-right">{r.overdue > 0 ? <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><TriangleAlert className="h-3 w-3" />{r.overdue}</span> : <span className="text-muted-foreground">0</span>}</td>
                      <td className="py-2 px-4 text-right">{r.avg_days ?? 0}d</td>
                      <td className={`py-2 pl-4 text-right ${(r.oldest_days ?? 0) >= (queue?.sla_days ?? 7) ? "text-red-700 font-semibold" : ""}`}>{r.oldest_days ?? 0}d</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Chapters awaiting review — flag delays */}
      <Card className="p-6" data-testid="oversight-queue">
        <div className="flex items-center gap-2 mb-1"><AlarmClock className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Chapters awaiting review</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Flag a delay to nudge the supervisor by notification and email. Overdue items are highlighted.</p>
        {!queue ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin brand-text" /></div>
          : queue.items.length === 0 ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nothing is awaiting review right now.</div>
          ) : (
            <div className="space-y-2">
              {queue.items.map((it) => {
                const flagged = flaggedChapterIds.has(it.chapter_id);
                return (
                  <div key={it.chapter_id} data-testid={`oversight-queue-item-${it.chapter_id}`}
                    className={`flex items-center justify-between gap-3 rounded-md border p-3 ${it.overdue ? "border-red-200 bg-red-50/60" : "border-border"}`}>
                    <button className="min-w-0 text-left flex-1" onClick={() => navigate(`/supervisions/${it.supervision_id}`)}>
                      <p className="text-sm font-medium truncate">{it.title} <span className="text-muted-foreground font-normal">· v{it.version}</span></p>
                      <p className="text-xs text-muted-foreground truncate">{it.student_name} · {it.supervisor_name}</p>
                    </button>
                    <div className={`shrink-0 flex items-center gap-1 text-xs font-medium ${it.overdue ? "text-red-700" : "text-amber-700"}`}>
                      <Clock className="h-3.5 w-3.5" />{daysLabel(it.days_waiting)}
                    </div>
                    {flagged ? (
                      <Badge className="bg-amber-100 text-amber-800 gap-1 shrink-0"><Flag className="h-3 w-3" />Flagged</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="shrink-0" data-testid={`flag-btn-${it.chapter_id}`} onClick={() => { setFlagItem(it); setNote(""); }}>
                        <Flag className="h-4 w-4 mr-1" />Flag delay
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </Card>

      {/* Flags log */}
      <Card className="p-6" data-testid="flags-log">
        <div className="flex items-center gap-2 mb-4"><Flag className="h-4 w-4 brand-text" /><h3 className="font-heading font-semibold">Delay flags raised</h3></div>
        {flagsLog.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No delay flags have been raised.</p>
        ) : (
          <div className="space-y-2">
            {flagsLog.items.map((f) => (
              <div key={f.id} data-testid={`flag-row-${f.id}`} className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{f.chapter_title} <span className="text-muted-foreground font-normal">· {f.student_name}</span></p>
                  <p className="text-xs text-muted-foreground">Supervisor: {f.supervisor_name} · raised by {f.raised_by_name}</p>
                  {f.note && <p className="text-xs text-foreground mt-1 border-l-2 brand-border pl-2">{f.note}</p>}
                </div>
                <Badge className={f.status === "resolved" ? "bg-emerald-100 text-emerald-700 gap-1 shrink-0" : "bg-amber-100 text-amber-800 gap-1 shrink-0"}>
                  {f.status === "resolved" ? <><CheckCircle2 className="h-3 w-3" />Resolved</> : <><Clock className="h-3 w-3" />Open</>}
                </Badge>
              </div>
            ))}
          </div>
        )}
        {flagsLog.pages > 1 && (
          <div className="flex items-center justify-between mt-4" data-testid="flags-pagination">
            <p className="text-xs text-muted-foreground">Page {flagsLog.page} of {flagsLog.pages} · {flagsLog.total} flags</p>
            <div className="flex gap-2">
              <Button data-testid="flags-prev" variant="outline" size="sm" disabled={flagsLog.page <= 1} onClick={() => loadFlagsLog(flagsLog.page - 1)}>Previous</Button>
              <Button data-testid="flags-next" variant="outline" size="sm" disabled={flagsLog.page >= flagsLog.pages} onClick={() => loadFlagsLog(flagsLog.page + 1)}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={!!flagItem} onOpenChange={(v) => !v && setFlagItem(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Flag a delay</DialogTitle>
            <DialogDescription>Notify the supervisor that this chapter needs their attention.</DialogDescription>
          </DialogHeader>
          {flagItem && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Nudge <strong>{flagItem.supervisor_name}</strong> about <strong>{flagItem.title}</strong> for {flagItem.student_name} (waiting {daysLabel(flagItem.days_waiting)}).
              </p>
              <Textarea data-testid="flag-note" rows={3} placeholder="Optional note to the supervisor (e.g. student has been waiting since…)" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          )}
          <DialogFooter>
            <Button data-testid="flag-submit" onClick={submitFlag} disabled={busy} className="brand-bg text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Flag className="h-4 w-4 mr-1" />Flag & notify supervisor</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
